// v2 larch — an open larch: a slim trunk visible as a full-height mast, hung with
// five wide, flat umbrella pads (drooping foliage discs) separated by clear air
// gaps so the tree reads soft and sparse, the pads running pale gold-green below
// to autumn gold at the small tip. A couple of thin drooping twig stubs fill the
// gaps. Variants re-roll the pad radii, the twigs and the gold-green palette
// deterministically. Ported 1:1 from age-of-mcp's v2/tree-larch.ts, using the
// library's shared `tree` (h01, lerp, pick, tier) helpers.
import * as THREE from "three";
import { defineModel, tree, type Part } from "../../lib";

const PALETTES = [
  { low: "#8e9a40", mid: "#a4b04e", hi: "#bac45c", gold: "#cfd06a" },
  { low: "#879543", mid: "#9caa51", hi: "#b1bd60", gold: "#c5ca6e" },
  { low: "#969e3c", mid: "#adb449", hi: "#c4c857", gold: "#d8d365" },
] as const;

const BARK = "#6a4f2e";

// [y, r, h, tone] per pad — wide, short cones read as drooping umbrellas;
// the air gaps between them show the trunk.
const PADS: readonly [number, number, number, 0 | 1 | 2 | 3][] = [
  [0.5, 0.3, 0.2, 0],
  [0.82, 0.25, 0.18, 1],
  [1.12, 0.2, 0.17, 1],
  [1.4, 0.15, 0.16, 2],
  [1.62, 0.1, 0.14, 3],
];

function larchParts(v: number): Part[] {
  const seed = 173 + v * 23;
  const pal = tree.pick(PALETTES, v);
  const tones = [pal.low, pal.mid, pal.hi, pal.gold] as const;
  const parts: Part[] = [
    // the full-height mast of a trunk
    { geometry: new THREE.CylinderGeometry(0.032, 0.06, 1.55, 6),
      color: BARK, position: [0, 0.775, 0] },
  ];
  for (let i = 0; i < PADS.length; i++) {
    const [y, r, h, tone] = PADS[i]!;
    const jitter = tree.lerp(0.92, 1.08, tree.h01(seed, 5 + i));
    parts.push(tree.tier(seed, 10 + i * 3, y, r * jitter, h, tones[tone], 7, 0.025));
  }
  // thin drooping twig stubs in the air gaps
  for (let k = 0; k < 3; k++) {
    const ang = tree.h01(seed, 40 + k) * Math.PI * 2;
    const ty = 0.66 + k * 0.3 + tree.h01(seed, 44 + k) * 0.06;
    parts.push({
      geometry: new THREE.CylinderGeometry(0.009, 0.015, 0.22, 4),
      color: BARK,
      position: [Math.cos(ang) * 0.1, ty, Math.sin(ang) * 0.1],
      rotation: [Math.sin(ang) * 1.75, 0, Math.cos(ang) * 1.75],
    });
  }
  // small gold tip
  parts.push(tree.tier(seed, 50, 1.66, 0.045, 0.14, pal.gold, 5, 0.005));
  return parts;
}

export const treeLarchV2 = defineModel({
  id: "tree-larch-v2",
  batchId: "fable",
  name: "Tree larch (v2)",
  tags: ["scenery","tree","larch","low-poly","age-of-mcp","fable"],
  summary: "Low-poly tree larch.",
  description: "Ported from the age-of-mcp gallery, v2 style.",
  author: "age-of-mcp",
  footprint: 0.44,
  variants: [
    { id: "variant-0", label: "Variant 1", parts: () => larchParts(0) },
    { id: "variant-1", label: "Variant 2", parts: () => larchParts(1) },
    { id: "variant-2", label: "Variant 3", parts: () => larchParts(2) },
  ],
  animations: [],
});
